import { lockScroll, unlockScroll, prefersReducedMotion } from '@theme/utilities';

/** Matches the dialog slide duration in header-drawer-boots.liquid. */
const DRAWER_TRANSITION_MS = 300;
/** Matches the panel slide duration in header-drawer-boots.liquid. */
const PANEL_TRANSITION_MS = 500;

/**
 * Boutique drawer menu: a modal `<dialog>` with slide-in panels and accordions.
 * Actions are declared in markup with `data-drawer-action`.
 */
class HeaderDrawerBoots extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.dialog?.addEventListener('cancel', this.#onCancel);
    this.dialog?.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.dialog?.removeEventListener('cancel', this.#onCancel);
    this.dialog?.removeEventListener('click', this.#onBackdropClick);
    clearTimeout(this.#closeTimer);
    unlockScroll(this);
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    return this.querySelector('dialog');
  }

  /** @returns {HTMLElement | null} */
  get toggleButton() {
    return this.querySelector('[data-drawer-action="open"]');
  }

  open() {
    const { dialog } = this;
    if (!dialog || dialog.open) return;

    clearTimeout(this.#closeTimer);
    dialog.showModal();
    lockScroll(this);
    this.toggleButton?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => dialog.classList.add('is-open'));
  }

  close() {
    const { dialog } = this;
    if (!dialog?.open) return;

    dialog.classList.remove('is-open');
    this.toggleButton?.setAttribute('aria-expanded', 'false');

    const finish = () => {
      dialog.close();
      this.#resetPanels();
      unlockScroll(this);
      this.toggleButton?.focus();
    };

    clearTimeout(this.#closeTimer);
    if (prefersReducedMotion()) {
      finish();
    } else {
      this.#closeTimer = setTimeout(finish, DRAWER_TRANSITION_MS);
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const control = event.target instanceof Element ? event.target.closest('[data-drawer-action]') : null;
    if (!(control instanceof HTMLElement)) return;

    switch (control.dataset.drawerAction) {
      case 'open':
        this.open();
        break;
      case 'close':
        this.close();
        break;
      case 'panel-open':
        this.#openPanel(control);
        break;
      case 'panel-close':
        this.#closePanel(control);
        break;
      case 'accordion':
        this.#toggleAccordion(control);
        break;
    }
  };

  /**
   * Escape closes with the slide animation instead of the native instant close.
   * @param {Event} event
   */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /**
   * Clicks on the backdrop target the dialog element itself.
   * @param {MouseEvent} event
   */
  #onBackdropClick = (event) => {
    if (event.target === this.dialog) this.close();
  };

  /**
   * @param {HTMLElement} trigger
   */
  #openPanel(trigger) {
    const panel = this.#controlledElement(trigger);
    if (!panel) return;

    panel.inert = false;
    panel.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');

    const backButton = panel.querySelector('[data-drawer-action="panel-close"]');
    if (backButton instanceof HTMLElement) backButton.focus({ preventScroll: true });
  }

  /**
   * @param {HTMLElement} control - An element inside the panel to close.
   */
  #closePanel(control) {
    const panel = control.closest('[data-drawer-panel]');
    if (!(panel instanceof HTMLElement)) return;

    panel.classList.remove('is-open');
    const trigger = this.querySelector(`[aria-controls="${panel.id}"]`);
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus({ preventScroll: true });
    }

    setTimeout(() => {
      if (!panel.classList.contains('is-open')) panel.inert = true;
    }, prefersReducedMotion() ? 0 : PANEL_TRANSITION_MS);
  }

  /**
   * @param {HTMLElement} trigger
   */
  #toggleAccordion(trigger) {
    const content = this.#controlledElement(trigger);
    if (!content) return;

    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!expanded));
    content.inert = expanded;
  }

  /** Return every panel and accordion to its collapsed state. */
  #resetPanels() {
    this.querySelectorAll('[data-drawer-panel].is-open').forEach((panel) => {
      panel.classList.remove('is-open');
      if (panel instanceof HTMLElement) panel.inert = true;
    });
    this.querySelectorAll('[data-drawer-action="panel-open"], [data-drawer-action="accordion"]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      const content = this.#controlledElement(/** @type {HTMLElement} */ (trigger));
      if (content && trigger.matches('[data-drawer-action="accordion"]')) content.inert = true;
    });
  }

  /**
   * @param {HTMLElement} trigger
   * @returns {HTMLElement | null}
   */
  #controlledElement(trigger) {
    const id = trigger.getAttribute('aria-controls');
    if (!id) return null;
    const element = this.querySelector(`#${CSS.escape(id)}`);
    return element instanceof HTMLElement ? element : null;
  }
}

if (!customElements.get('header-drawer-boots')) {
  customElements.define('header-drawer-boots', HeaderDrawerBoots);
}
